'use client';
import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchViewParams, extractParamData, extractDefaultParams } from '@/lib/dataHandlers/fetch-view-params';
import { replaceParams, fetchAPIData } from '@/lib/dataHandlers/DataFetchers';
import { appendParamsToURL } from '@/lib/dataHandlers/URLBuilders';
import DataContext from '@/contexts/DataContext';
import { config } from '@/config';
import { useComponentLogger } from '@/lib/component-logger';
import _ from 'lodash';
import { Page as Loading } from '@/pages/dashboard/loading';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GroupedCard } from '@/components/widgets/quick-stats/grouped-card';
import { SelectDateTextWrapper } from '@/components/widgets/inputs/select-date-text-wrapper';
import { TreeAccordion } from '@/components/widgets/tree/tree-accordion';
import CircularProgress from '@mui/material/CircularProgress';


export function ViewPage() {
  const logger = useComponentLogger("View Page");
  const [searchParams] = useSearchParams();
  const { currentProject, currentApp, currentView, setCurrentView, viewParams, setViewParams, prelimParamCount, setPrelimParamCount, selectedViewParams, setSelectedViewParams, changedParams, setChangedParams, previousURL, setPreviousURL, setExternalURL, doneValidating } = useContext(DataContext);
  const [previousParams, setPreviousParams] = useState({});
  const [doneFetching, setDoneFetching] = useState(true);
  const [contentTitle, setContentTitle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    logger.debug('Mounted');

    return () => {
      logger.debug('Unmounted');
    };
  }, []); 
  

  const fetchChildParams = async (viewParams, changedParams) => {
    logger.debug('fetch child params start', viewParams, changedParams, selectedViewParams);

    for (const selectedParam in selectedViewParams) {
      // logger.debug('is param changed', selectedParam, changedParams?.includes(selectedParam));
      if (!changedParams?.includes(selectedParam)) continue;
      let matchedIndex = viewParams?.findIndex((param) => param.name === selectedParam);
      // logger.debug('for cur param', selectedParam, matchedIndex, viewParams[matchedIndex], selectedViewParams);

      if (matchedIndex !== null && matchedIndex !== undefined && matchedIndex < (prelimParamCount - 1) && viewParams[matchedIndex]?.childParams) {
        let childParamData = [];
        for (const childParam of viewParams[matchedIndex].childParams) {
            // if (selectedViewParams[childParam.name]) {
            //   let oldParams = {...selectedViewParams}
            //   delete oldParams[childParam.name];
            //   setSelectedViewParams(oldParams)
            // } 
            // delete selectedViewParams[childParam.name];
            logger.debug('new child start', childParam, selectedViewParams, viewParams);
            let childParamObject = await extractParamData(childParam, currentProject?.id, selectedViewParams);
            logger.debug('new child', childParamObject, selectedParam, selectedParam?.name);
            childParamObject['parentName'] = selectedParam;
            childParamData.push(childParamObject);
        }

        logger.debug('fetch child end', childParamData, viewParams);

        let newViewParams = [...viewParams];
        newViewParams[matchedIndex]['childParams'] = childParamData;
        logger.debug('set params: viewpage');
        setViewParams(newViewParams);

        logger.debug('new params end', selectedViewParams, viewParams, newViewParams, childParamData);
        
      }        
    }

  }


  const updateURLParams = (url, params, previousURL, replace) => {
    let newURL = url.replace(/&params.*/, '');
    newURL = appendParamsToURL(newURL, params);

    logger.debug('new view url:', newURL, previousURL, newURL === previousURL, replace, params, selectedViewParams);

    if (previousURL === null || newURL.replace('/', '') !== previousURL.replace('/', '')) {
      setPreviousURL(newURL);
      setExternalURL(false);
      logger.debug('navigate: view page param change', replace, newURL, params);
      navigate(newURL, {replace: replace});
    }

    setPreviousURL(newURL.replace('/', ''));
  }

  const updateContentTitle = async () => {
    const viewData = currentView?.viewData;
    if (!viewData) return;
    if (!viewData.title || !viewData.title?.dataSource) {
      setContentTitle(null);
      return;
    }

    const title = viewData?.title;
    if (title?.dataSource?.type === "text") {
      
      setContentTitle(title.dataSource.text);
    } else if (title?.dataSource?.type === "apiExpr") {
      const parsedProject = currentProject?.id?.replace('p:', '');
      const url = `https://data.clistek.io/api/${parsedProject}/eval?expr=`;
      let expr = title?.dataSource?.expr;
      
      logger.debug('replace params: update title', selectedViewParams)
      let formattedExpr = replaceParams(expr, selectedViewParams);

      logger.debug("view expr", formattedExpr, expr, selectedViewParams);
      const data = await fetchAPIData(url, formattedExpr, true);

      // logger.debug('view api data', data);

      if (data?.rows && data.rows.length > 0) {
        setContentTitle(data.rows[0].val);
      }
    }
  }


  useEffect(() => {
    setDoneFetching(false);
    logger.debug('new view url params', currentView, selectedViewParams, previousParams, prelimParamCount );
    
    // check changed params
    let newParams = [];
    let replace = false;
    for (const paramType in selectedViewParams) {
      logger.debug('changed param:', paramType, previousParams, selectedViewParams);
      
      if (Array.isArray(selectedViewParams[paramType]?.value) || Array.isArray(previousParams[paramType])) {
        const newArr = selectedViewParams[paramType].value ?? [];
        const oldArr = previousParams[paramType] ?? [];
        // const symmetricDiff = _.xor(oldArr, newArr); // find if the arrays have any differing vals (a val is in 1 arr but not he other)
        const symmetricDiff = _.xorBy(oldArr, newArr, 'value'); // find if the arrays have any differing vals (a val is in 1 arr but no the other)
        logger.debug("changed array", symmetricDiff, newArr, oldArr);
        if (symmetricDiff.length > 0) {
          newParams.push(paramType);
          if (!previousParams[paramType] && symmetricDiff.includes(selectedViewParams[paramType]?.default)) replace = true;
        }
      } else {
        if (!previousParams[paramType] || previousParams[paramType].value !== selectedViewParams[paramType].value ) {
          newParams.push(paramType);
          if (!previousParams[paramType] && selectedViewParams[paramType].value === selectedViewParams[paramType].default) replace = true;
        }
      }
    }
    logger.debug('set changed:', newParams, previousParams);
    setPreviousParams(selectedViewParams);
    setChangedParams(newParams);

    // fetch childparams if parent changed
    logger.debug('changed params', newParams, previousParams, selectedViewParams);
    // if (newParams.length > 0 || Object.keys(selectedViewParams) !== Object.keys(previousParams)) {
     if (newParams.length > 0) {
      updateContentTitle();
      updateURLParams(window.location.search, selectedViewParams, previousURL, replace);
      fetchChildParams(viewParams, newParams).then(() => {
        setDoneFetching(true);
        // generateSelectors();
      });

    }

    // setPreviousParams(selectedViewParams);
    // logger.debug('set changed:', newParams, previousParams)
    // setChangedParams(newParams);
  }, [selectedViewParams]);

  useEffect(() => {
    logger.debug('done fetching', doneFetching);
    if (doneFetching) generateSelectors();
  }, [doneFetching]);

  useEffect(() => {
    logger.debug('viewpage', currentView?.id, searchParams.get('viewID'), viewParams, prelimParamCount, selectedViewParams);
    updateContentTitle();
  }, [currentView]);

  const generateSelectors = () => {
    logger.debug('gen selectors', viewParams);
    if (viewParams === null || !Array.isArray(viewParams) || viewParams.length === 0) return null;
    
    return viewParams?.map((param) => {
      let selectorGroup = [] // to account for parent/child selectors
      logger.debug('cur param', param);

      if (param.type === 'navTreeApiExpr') {
        selectorGroup.push(<TreeAccordion treeObject={param} treeParams={selectedViewParams} setTreeParams={setSelectedViewParams}/>);
      } else if (param.type === 'object') {
        // do nothing
      } else selectorGroup.push(<SelectDateTextWrapper key={`selector-${param.name}`} config={param}/>);

      if (param.childParams) {
        param.childParams.map((childParam) => {
          logger.debug('cur param child', childParam)
          if (childParam.type === 'object') {
            // do nothing
            return selectorGroup;
          } else return selectorGroup.push(<SelectDateTextWrapper key={`selector-${childParam.name}`} config={childParam}/>)
        })
      }
      return selectorGroup
    })
  }



  const generateGroups = () => {
    if (currentView?.viewData === null || currentView?.viewData === undefined) return null;
    const groups = currentView.viewData.groups || null;
    if (groups === null || groups === undefined || groups.length === 0) return null;

    return groups.map((group, index) => (<GroupedCard config={group} params={selectedViewParams} isLoading={isLoading} setIsLoading={setIsLoading} key={index} />));
  }

  

  const normalize = (arr) => [...arr].filter(Boolean).sort();
  // wait for the viewParams load and match the view definition params before rendering selectors
  // prevents the params + content of the prev page from briefly showing on page load
  let curParamKeys = viewParams.map(param => param.name || null); // the params that we currently have
  let viewParamKeys = currentView?.viewData?.parameters?.map(param => param.name || null); // the params that this view is supposed to have
  // let viewParamKeys = viewForRender?.viewData?.parameters?.map(param => param.name || null); // the params that this view is supposed to have
  // const selectorsReady = currentView?.viewData?.parameters ? _.isEqual(normalize(curParamKeys), normalize(viewParamKeys)) : false;

  const requiredParams = currentView?.viewData?.parameters?.map(p => p.name) ?? [];
  // const requiredParams = viewForRender?.viewData?.parameters?.map(p => p.name) ?? [];

  const selectorsReady = requiredParams.every(name => curParamKeys.includes(name));

  logger.debug('params ready', selectorsReady, curParamKeys, viewParamKeys, prelimParamCount, selectedViewParams, currentView);

  useEffect(() => {
    logger.debug('view param change', viewParams, currentView, selectorsReady)
  }, [viewParams, currentView]);

  if (currentView?.id.replace('@','') !== searchParams.get('viewID')?.replace('@','')) {
    return <Loading />;
  }


  return (
    <Box
        sx={{
          // maxWidth: 'var(--Content-maxWidth)',
          m: 'var(--Content-margin)',
          p: 'var(--Content-padding)',
          width: 'var(--Content-width)',
          alignItems:'center'
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3} >
            <Box sx={{ flex: '1 1 auto' }}>
              <Typography variant="h4">{currentView?.name ?? ""}</Typography>
              {/* <Typography variant="h4">{contentTitle || ""}</Typography> */}
            </Box>
            <>
            {selectorsReady ? generateSelectors() : <></>}
            
            {/** check that length >= prelimParamCount bc the number of selected params can exceed the perlim param (e.g. if a page has a map, which acts as a param) */}
            {selectorsReady && (Object.keys(selectedViewParams).length >= prelimParamCount) ?
                <>
                {contentTitle !== null && contentTitle !== undefined && contentTitle !== 'null' ? 
                  <Typography variant="h5">{contentTitle}</Typography>
                  : <></>
                }
                <Grid container spacing={3}>
                  {/* Render objects once all params are selected */}
                  {generateGroups()}
                </Grid> 
                </>  
              : <></>
            }
            
            </>
          </Stack>
        </Stack>
    </Box>
  );
}
